import {useState, useMemo, ReactElement} from 'react';
import classnames from 'classnames';
import Tabs, {TABS_TYPES} from '@frontend/ui-kit/Components/Tabs';
import Benefit from '../Benefit';
import {equal} from '../../../utils';
import benefitsWallet from '../../../static/images/benefitsWallet.png';
import benefitsConcierge from '../../../static/images/benefitsConcierge.png';
import benefitsRxSavings from '../../../static/images/benefitsRxSavings.png';
import benefitsFindCare from '../../../static/images/benefitsFindCare.png';
import {gettext} from '../../../translationService';
import analyticsService, {ANALYTICS_EVENTS} from '../../../analyticsService';
import './index.scss';

const BENEFITS = {
    wallet: {
        tabName: gettext('Wallet'),
        analyticsTabName: 'wallet',
        title: gettext('View all of your benefits cards in one place'),
        text: [
            gettext('Access digital versions of your benefits cards in your HealthJoy wallet. Always available and always up-to-date.'),
            gettext('With a convenient benefits wallet, live Healthcare Concierge support and personalized recommendations, we help you save time and money to lead a healthier life.')
        ],
        image: benefitsWallet
    },
    concierge: {
        tabName: gettext('Concierge'),
        analyticsTabName: 'concierge',
        title: gettext('Our concierge team is here to answer any of your benefits questions'),
        text: [
            gettext('Save time and money with help from our Healthcare Concierge experts. They can find you a local doctor, set appointments, answer questions, and much more.'),
            gettext('Our Healthcare Concierges and Clinical Coaches will guide you to the right benefits at the right moment in your care journey through personalized health recommendations.')
        ],
        image: benefitsConcierge
    },
    findCare: {
        tabName: gettext('Find Care'),
        analyticsTabName: 'findCare',
        title: gettext('Finding a healthcare provider has never been easier'),
        text: [
            gettext('Search and filter through a robust list of doctors, facilities, and pharmacies compatible with your health plan that meet your unique preferences using our Find Care tool. Once you\'ve found the doctor or facility you\'re looking for, you can save them to your profile to easily reference again.'),
            gettext('Ready for your next appointment or have any questions? Our Healthcare Concierge team is happy to answer any questions you may have and can schedule an appointment on your behalf.')
        ],
        image: benefitsFindCare
    },
    rxSavings: {
        tabName: gettext('Rx Savings'),
        analyticsTabName: 'rxSavings',
        title: gettext('Medication savings'),
        text: [
            gettext('Drug prices can vary greatly — use our Rx Savings program to help ensure you are not overpaying for your medications and infusion therapies.'),
            gettext('It\'s super simple. Just enter some basic information on any medications you are taking, and then we will do the research and send you ways you can save.')
        ],
        image: benefitsRxSavings
    }
};

export const analyticsTabNames = Object.values(BENEFITS).map(({analyticsTabName}) => analyticsTabName);

const Benefits = (): ReactElement => {
    const [activeBenefitIndex, setActiveBenefitIndex] = useState(0);
    const tabs = useMemo(() => Object.values(BENEFITS).map(({tabName}) => gettext(tabName)), [BENEFITS]);

    const onChangeTab = index => {
        analyticsService.track(ANALYTICS_EVENTS.activation.promotionTabClicked, {tabName: analyticsTabNames[index]});
        setActiveBenefitIndex(index);
    };

    const getBenefit = ([key, value], index) => {
        const isActive = equal(index, activeBenefitIndex);
        const benefitProps = {
            ...value,
            title: gettext(value.title),
            text: value.text.map(gettext),
            className: classnames('product-info-benefits-item', `product-info-benefits-item__${key}`, {
                'product-info-benefits-item_is-active': isActive
            })
        };

        return <Benefit {...benefitProps}/>;
    };

    return (
        <div className='product-info-benefits'>
            <Tabs type={TABS_TYPES.type2}
                className='product-info-benefits-tabs'
                activeTabClassName='product-info-benefits-tabs__active-tab'
                tabs={tabs}
                activeTab={activeBenefitIndex}
                onChange={onChangeTab}/>

            {Object.entries(BENEFITS).map(getBenefit)}
        </div>
    );
};

export {Benefits as TestableBenefits, BENEFITS};
export default Benefits;
